import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import HistoricoEstoque from "@/components/HistoricoEstoque";
import ImageUpload from "@/components/ImageUpload";
import AuthImage from "@/components/AuthImage";
import { ProducaoTab, PROD_EMPTY, FUNCOES, prodPayload } from "@/components/produto/ProducaoTab";
import MigracaoCadastro from "@/components/produto/MigracaoCadastro";
import { useConfirmSaldo } from "@/components/produto/SaldoSemLoteDialog";
import { Loader2, Plus, Pencil, Package, Trash2, MoreVertical, History, Power, PowerOff, Factory, GitMerge } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nome: "", marca: "", sku: "", ean: "", ean_master: "", fator_conversao: 1, categoria: "Secos", unidade_medida: "UN", estoque_minimo: 0, foto_url: "", ativo: true, distribuivel: true, produto_base_nome: "" };
const FILTROS = { TODOS: "Todos", PRODUCAO: "Habilitados p/ produção", PEDIDOS: "Disponíveis p/ pedidos", SO_PRODUCAO: "Só produção" };
const errDetail = (e) => e.response?.data?.detail;
const errText = (e) => { const d = errDetail(e); return typeof d === "string" ? d : d?.msg || "Erro ao salvar"; };

async function comConfirmacao(fn, ask) {
  try { return await fn(false); } catch (e) {
    const d = errDetail(e);
    if (e.response?.status !== 409 || !d?.requer_confirmacao) throw e;
    if (!(await ask(d))) throw Object.assign(new Error("cancelado"), { cancelado: true });
    return fn(true);
  }
}

function ProdutoCard({ p, ext, podeEditar, isAdmin, onEdit, onHist, onToggle, onDel }) {
  return (
    <Card className="bg-card/70 p-4" data-testid={`produto-${p.sku}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-md bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
          {p.foto_url ? <AuthImage path={p.foto_url} alt={p.nome} className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold truncate">{p.nome}</div>
            {p.ativo === false && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 font-mono" data-testid={`prod-inativo-${p.sku}`}>Inativo</span>}
            {p.distribuivel === false && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground border border-border font-mono" data-testid={`prod-so-producao-${p.sku}`}>Só produção</span>}
          </div>
          <div className="text-xs text-muted-foreground">{p.marca || "—"} · {p.categoria}</div>
          <div className="text-[10px] font-mono text-primary mt-1">{p.sku} · EAN {p.ean || "—"}</div>
          {ext?.producao_habilitada !== false && ext && <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`prod-funcoes-${p.sku}`}>
            <Factory className="w-3 h-3 text-amber-300" />{(ext.funcoes || []).map((f) => <span key={f} className="text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30">{FUNCOES[f] || f}</span>)}
            {ext.integracao_pendente && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-300 border border-red-500/30">integração pendente</span>}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-${p.sku}`}><Pencil className="w-4 h-4" /></Button>
          {podeEditar && <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`menu-prod-${p.sku}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={onHist} data-testid={`hist-${p.sku}`}><History className="w-4 h-4" /> Histórico</DropdownMenuItem>
              <DropdownMenuItem onClick={onToggle} data-testid={`toggle-prod-${p.sku}`}>{p.ativo === false ? <><Power className="w-4 h-4" /> Ativar</> : <><PowerOff className="w-4 h-4" /> Desativar</>}</DropdownMenuItem>
              {isAdmin && <><DropdownMenuSeparator /><DropdownMenuItem className="text-red-400" onClick={onDel} data-testid={`del-${p.sku}`}><Trash2 className="w-4 h-4" /> Excluir</DropdownMenuItem></>}
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>
    </Card>
  );
}

function LogisticaTab({ form, set, catOpts, disabled }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="produto-aba-logistica">
      <div className="sm:col-span-2"><Label>Nome do Produto</Label><Input value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Ex.: Coxinha de frango 100 g" disabled={disabled} data-testid="f-nome" /></div>
      <div><Label>Marca</Label><Input value={form.marca} onChange={(e) => set("marca", e.target.value)} disabled={disabled} data-testid="f-marca" /></div>
      <div><Label>SKU interno <span className="text-muted-foreground font-normal normal-case">(único; vazio = automático)</span></Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="Auto: UNI000001" disabled={disabled} data-testid="f-sku" /></div>
      <div><Label>EAN Unidade <span className="text-muted-foreground font-normal normal-case">(opcional)</span></Label><Input value={form.ean} onChange={(e) => set("ean", e.target.value)} disabled={disabled} data-testid="f-ean" /></div>
      <div><Label>EAN Master (caixa)</Label><Input value={form.ean_master} onChange={(e) => set("ean_master", e.target.value)} disabled={disabled} data-testid="f-ean-master" /></div>
      <div><Label>Unidades por caixa master</Label><Input type="number" value={form.fator_conversao} onChange={(e) => set("fator_conversao", e.target.value)} disabled={disabled} data-testid="f-fator" /></div>
      <div><Label>Unidade de estoque/pedido</Label><Input value={form.unidade_medida} onChange={(e) => set("unidade_medida", e.target.value)} disabled={disabled} data-testid="f-un" /></div>
      <div><Label>Categoria</Label>
        <Select value={form.categoria} onValueChange={(v) => set("categoria", v)} disabled={disabled}><SelectTrigger data-testid="f-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>{catOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      <div><Label>Estoque mínimo ({form.unidade_medida})</Label><Input type="number" value={form.estoque_minimo} onChange={(e) => set("estoque_minimo", e.target.value)} disabled={disabled} data-testid="f-minimo" /></div>
      <label className="sm:col-span-2 flex items-center justify-between rounded-md border border-border p-3 cursor-pointer">
        <div><div className="text-sm font-semibold">Disponível para pedidos</div><div className="text-[11px] text-muted-foreground">Desligado = uso só na produção (as lanchonetes não veem).</div></div>
        <Switch checked={form.distribuivel !== false} onCheckedChange={(v) => set("distribuivel", v)} disabled={disabled} data-testid="f-distribuivel" />
      </label>
      {!disabled && <div className="sm:col-span-2"><ImageUpload label="Foto do produto" value={form.foto_url} onChange={(x) => set("foto_url", x || "")} testid="produto-foto" /></div>}
    </div>
  );
}

export default function Produtos() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isAdmin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const podeEditar = ["SUPER_ADMIN", "MASTER", "ADMIN", "GESTOR", "SUPERVISOR"].includes(user.role);
  const [produtos, setProdutos] = useState(null);
  const [exts, setExts] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [forn, setForn] = useState([]);
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState("logistica");
  const [form, setForm] = useState(EMPTY);
  const [prod, setProd] = useState(PROD_EMPTY);
  const [travado, setTravado] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [histId, setHistId] = useState(null);
  const [mig, setMig] = useState(false);
  const [busca, setBusca] = useState("");
  const [askSaldo, saldoDialog] = useConfirmSaldo();
  const filtro = { producao: "PRODUCAO", pedidos: "PEDIDOS", so_producao: "SO_PRODUCAO" }[params.get("filtro")] || "TODOS";

  const load = () => {
    api.get("/produtos").then((r) => setProdutos(r.data));
    api.get("/producao/itens").then((r) => setExts(Object.fromEntries(r.data.map((i) => [i.produto_id || i.sku_id, i]).filter(([k]) => k)))).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/categorias").then((r) => setCategorias(r.data.filter((c) => c.ativo))).catch(() => {});
    api.get("/fornecedores").then((r) => setForn(r.data)).catch(() => {});
  }, []);

  const open = async (p) => {
    setTab("logistica"); setTravado(false);
    if (!p) { setForm(EMPTY); setProd(PROD_EMPTY); setEditId(null); setModal(true); return; }
    setForm({ ...EMPTY, ...p, produto_base_nome: "" }); setEditId(p.id); setProd(PROD_EMPTY); setModal(true);
    try {
      const r = (await api.get(`/producao/cadastro/${p.id}`)).data;
      if (r.producao) setProd({ ...PROD_EMPTY, ...r.producao, producao_habilitada: r.producao.producao_habilitada ?? r.producao.ativo !== false });
      setTravado(r.tem_historico);
    } catch (e) { /* perfil sem acesso à produção */ }
  };

  const save = async () => {
    if (!form.nome) return toast.error("Nome é obrigatório");
    setSaving(true);
    try {
      const payload = { ...form, fator_conversao: Number(form.fator_conversao) || 1, estoque_minimo: Number(form.estoque_minimo) || 0 };
      if (!editId && !payload.produto_base_nome) payload.produto_base_nome = payload.nome;
      const salvo = editId
        ? (await comConfirmacao((c) => api.put(`/produtos/${editId}`, { ...payload, confirmar_saldo_existente: c }), askSaldo)).data
        : (await api.post("/produtos", payload)).data;
      if (prod.producao_habilitada || exts[salvo.id]) {
        await comConfirmacao((c) => api.put(`/producao/cadastro/${salvo.id}`, { ...prodPayload(prod), confirmar_saldo_existente: c }), askSaldo);
      }
      toast.success(editId ? "Produto atualizado" : "Produto criado");
      setModal(false); load();
    } catch (e) { if (!e.cancelado) toast.error(errText(e)); else toast.message("Nada foi alterado na produção"); load(); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Excluir o produto "${p.nome}"? Só é possível se não houver histórico logístico nem de produção.`)) return;
    try { await api.delete(`/produtos/${p.id}`); toast.success("Produto excluído"); load(); } catch (e) { toast.error(errText(e)); }
  };
  const toggleAtivo = async (p) => {
    try { await api.put(`/produtos/${p.id}`, { ...p, ativo: p.ativo === false }); toast.success(p.ativo === false ? "Produto reativado" : "Produto desativado"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setProd((f) => ({ ...f, [k]: v }));
  const catOpts = categorias.length ? categorias.map((c) => c.nome) : ["Secos", "Congelados", "Laticínios", "Hortifrúti", "Embalagens"];
  const lista = (produtos || []).filter((p) => {
    const e = exts[p.id];
    const ok = { TODOS: true, PRODUCAO: e && e.producao_habilitada !== false && e.ativo !== false, PEDIDOS: p.distribuivel !== false, SO_PRODUCAO: p.distribuivel === false }[filtro];
    return ok && `${p.sku} ${p.nome} ${p.ean}`.toLowerCase().includes(busca.toLowerCase());
  });
  const setFiltro = (k) => setParams(k === "TODOS" ? {} : { filtro: { PRODUCAO: "producao", PEDIDOS: "pedidos", SO_PRODUCAO: "so_producao" }[k] });

  return (
    <div className="space-y-5" data-testid="produtos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Produtos</h1>
          <div className="text-xs text-muted-foreground mt-1">Cadastro único da logística e da produção.</div>
        </div>
        <div className="flex gap-2">
          {isAdmin && <Button variant="outline" onClick={() => setMig(true)} data-testid="btn-migracao-cadastro"><GitMerge className="w-4 h-4" /> Migração</Button>}
          {podeEditar && <Button onClick={() => open(null)} data-testid="btn-novo-produto"><Plus className="w-4 h-4" /> Novo Produto</Button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {Object.entries(FILTROS).map(([k, v]) => <Button key={k} size="sm" variant={filtro === k ? "default" : "outline"} onClick={() => setFiltro(k)} data-testid={`filtro-produtos-${k}`}>{v}</Button>)}
        <Input placeholder="Buscar SKU, nome ou EAN..." value={busca} onChange={(e) => setBusca(e.target.value)} className="w-full sm:w-64 sm:ml-auto" data-testid="produtos-busca" />
      </div>
      {!produtos ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lista.map((p) => <ProdutoCard key={p.id} p={p} ext={exts[p.id]} podeEditar={podeEditar} isAdmin={isAdmin}
            onEdit={() => open(p)} onHist={() => setHistId(p.id)} onToggle={() => toggleAtivo(p)} onDel={() => del(p)} />)}
          {!lista.length && <div className="text-sm text-muted-foreground" data-testid="produtos-vazio">Nenhum produto neste filtro.</div>}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? (podeEditar ? "Editar" : "Ver") : "Novo"} Produto</DialogTitle></DialogHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList><TabsTrigger value="logistica" data-testid="tab-logistica">Logística</TabsTrigger><TabsTrigger value="producao" data-testid="tab-producao">Produção</TabsTrigger></TabsList>
            <TabsContent value="logistica"><LogisticaTab form={form} set={set} catOpts={catOpts} disabled={!podeEditar} /></TabsContent>
            <TabsContent value="producao"><ProducaoTab p={prod} setP={setP} fornecedores={forn} unLog={form.unidade_medida} bloqueado={travado} disabled={!podeEditar} /></TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Fechar</Button>
            {podeEditar && <Button onClick={save} disabled={saving} data-testid="btn-salvar-produto">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {saldoDialog}
      {mig && <MigracaoCadastro open={mig} onClose={() => setMig(false)} onDone={load} />}
      {histId && <HistoricoEstoque produtoId={histId} onClose={() => setHistId(null)} />}
    </div>
  );
}
